<template>
  <div class="cookie-consent" v-if="visible">
    <p class="cookie-consent__title">{{ $t('cookie_consent_title') }}</p>
    <p class="cookie-consent__text">{{ $t('cookie_consent_desc1') }}</p>
    <p class="cookie-consent__text">{{ $t('cookie_consent_desc2') }}</p>
    <a class="cookie-consent__link" :href="policyHref" target="_blank" rel="noopener">
      {{ $t('cookie_consent_policy_link') }}
    </a>
    <div class="cookie-consent__actions">
      <button type="button" class="cookie-consent__btn" @click="reject">
        {{ $t('cookie_consent_reject') }}
      </button>
      <button type="button" class="cookie-consent__btn cookie-consent__btn--accept" @click="accept">
        {{ $t('cookie_consent_accept') }}
      </button>
    </div>
  </div>
</template>

<script>
/**
 * Cookie Consent 横幅（GDPR，仅海外构建展示）
 *
 * 展示条件：海外构建 且 非 App 内嵌页 且 本浏览器没有有效的同意记录。
 * 与登录态无关 —— 同意的主体是浏览器，不是账号（见 utils/cookieConsent.js）。
 *
 * 非模态：不挡页面操作。用户一直不点 = 未同意 = 不启用数据分析（GDPR 是
 * opt-in 制，「继续浏览视为同意」不合规）。
 *
 * Accept 向外抛 accepted 事件，由 App.vue 当场把 Mixpanel 拉起来（不用刷新）。
 */
import { IS_CHINA } from '@/config'
import { isEmbed } from '@/embed/env'
import { getConsent, saveConsent, clearAnalyticsTraces, CONSENT_CHOICE } from '@/utils/cookieConsent'

export default {
  name: 'CookieConsent',
  data() {
    return {
      // App 内嵌页不弹（App 有自己的隐私协议流程）；国内构建不弹（无 GDPR 要求）
      visible: !IS_CHINA && !isEmbed() && !getConsent(),
    }
  },
  computed: {
    /**
     * 新标签页打开海外版隐私政策（静态文档），并定位到「7. COOKIES」。
     *
     * 地址按环境写死（产品指定）：正式 public.hskmock.com，其余（测试/联调）
     * staging-public.hskmock.com。不用 store 里的 mockPublicUrl —— 它是异步接口
     * 拿的，横幅首屏渲染时可能还没就绪。
     *
     * 定位靠文档里的真锚点：政策文档（Word 导出的静态 HTML）的
     * 「7. COOKIES」段落 <p> 上已加 id="cookies"（两个环境的文件都改过并
     * 重新上传），普通 hash 锚点全浏览器原生支持。曾试过 Text Fragment
     *（#:~:text=）免改文档，实测在该文档上不滚动，弃用。
     * ⚠️ 若日后重新导出/替换这份静态 HTML，记得给「7. COOKIES」段落
     * 保留 id="cookies"，否则链接退化为打开页面顶部。
     */
    policyHref() {
      const base =
        process.env.VUE_APP_MODE === 'prod'
          ? 'https://public.hskmock.com'
          : 'https://staging-public.hskmock.com'
      return base + '/static/mock_mobile_web/policy/china/en/privacy_policy.html#cookies'
    },
  },
  methods: {
    accept() {
      saveConsent(CONSENT_CHOICE.ACCEPTED)
      this.visible = false
      this.$emit('accepted')
    },
    reject() {
      saveConsent(CONSENT_CHOICE.REJECTED)
      // 此前 Accept 过又改主意的场景：把已写入的 mp_* 残留清掉
      clearAnalyticsTraces()
      this.visible = false
    },
  },
}
</script>

<style lang="scss" scoped>
.cookie-consent {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 3000;
  padding: 20px 56px 24px;
  // UI 稿的横幅明显透出底下内容,透明度按稿子调
  background: rgba(255, 255, 255, 0.75);
  box-shadow: 0 -2px 12px rgba(44, 47, 70, 0.08);

  &__title {
    margin: 0 0 10px;
    font-size: 16px;
    font-weight: 600;
    color: #2c2f46;
  }

  &__text {
    margin: 0 0 6px;
    font-size: 13px;
    line-height: 20px;
    color: #44444f;
  }

  &__link {
    display: inline-block;
    margin: 6px 0 14px;
    font-size: 13px;
    // UI 稿是深灰链接,不是浏览器默认蓝。带 !important 并显式覆盖
    // :visited/:hover,压住一切潜在的全局 a 样式/UA 默认色
    color: #2c2f46 !important;
    text-decoration: underline;
    cursor: pointer;

    &:visited,
    &:hover {
      color: #2c2f46 !important;
    }
  }

  &__actions {
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  &__btn {
    min-width: 200px;
    padding: 9px 24px;
    background: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 20px;
    font-size: 13px;
    color: #2c2f46;
    cursor: pointer;
    white-space: nowrap;
    transition: border-color 0.2s, color 0.2s;

    &:hover {
      border-color: #faae56;
      color: #faae56;
    }

    &--accept {
      min-width: 240px;
    }
  }
}

@media screen and (max-width: 768px) {
  .cookie-consent {
    padding: 16px;

    &__btn {
      flex: 1;
      min-width: 0;
    }
  }
}
</style>
